/**
 * Campañas (`/activa/segmentaciones`, DISENO §9 en su versión mínima): una tarjeta por campaña (cada segmentación es
 * una) con en qué paso quedó, sus grupos y el botón para seguir (`pasoDeCampana`). El nombre lleva a su reporte. Del
 * equipo.
 */

import { Link } from "react-router-dom";
import { ArrowRight, Layers, Plus } from "lucide-react";
import { EncabezadoPagina, EstadoVacio, Pagina } from "../../../components/activa/Pagina";
import { num } from "../../campana-nueva/utils/format";
import { cuandoSubida } from "../../base-ventas/logic/base";
import { usePedido } from "../../emails/hooks/usePedido";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { ResultadoEnCorto } from "../../envios/components/ResultadoEnCorto";
import { segmentacionesApi } from "../api";
import { btnPrimario, btnSecundario } from "../components/PasosCampana";
import { R_NUEVA_CAMPANA, fecha, nombreDelGrupo, pasoDeCampana, rutaGrupos, rutaSegmentar } from "../logic/segmentar";
import type { Segmentacion } from "../types";

const TONOS = { violeta: "bg-violet-50 text-violet-700", ambar: "bg-amber-50 text-amber-800", rojo: "bg-red-50 text-red-700", gris: "bg-slate-100 text-slate-600", verde: "bg-emerald-50 text-emerald-700" };

export default function CampanasPage() {
  const lista = usePedido<Segmentacion[]>(() => segmentacionesApi.listar(), "segmentaciones");
  const campanas = lista.dato ?? [];
  return (
    <Pagina>
      <EncabezadoPagina
        antetitulo="Campañas"
        titulo="Tus campañas"
        bajada="Cada vez que segmentás tu base nace una: sus grupos, un mensaje para cada uno y su envío."
        acciones={
          campanas.length > 0 && (
            <Link to={R_NUEVA_CAMPANA} className={btnPrimario}>
              <Plus size={16} aria-hidden="true" />
              Nueva campaña
            </Link>
          )
        }
      />
      {lista.error && !lista.dato && <ErrorTarjeta texto={lista.error} onReintentar={lista.recargar} />}
      {lista.cargando && !lista.dato && <Esqueleto alto="h-40" etiqueta="Cargando las campañas" />}
      {lista.dato && !campanas.length && (
        <EstadoVacio
          icono={Layers}
          titulo="Todavía no hay campañas."
          testId="sin-campanas"
          accion={
            <Link to={R_NUEVA_CAMPANA} className={btnPrimario}>
              <Plus size={16} aria-hidden="true" />
              Nueva campaña
            </Link>
          }
        >
          DEENEX parte tu base en grupos, el agente le escribe un mensaje a cada uno y una parte queda de control para
          medir cuánto vendiste de más.
        </EstadoVacio>
      )}
      <ul className="space-y-3">
        {campanas.map((c) => {
          const paso = pasoDeCampana(c);
          // el nombre lleva al reporte de la campaña (sus grupos) o, mientras se arma, a cómo va
          const ver = c.estado === "borrador" || c.estado === "nombres_pendientes" ? rutaGrupos(c.id) : rutaSegmentar(c.id);
          return (
            <li key={c.id} data-testid={`campana-${c.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <Link to={ver} className="font-semibold text-slate-900 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 rounded">
                    {c.nombre}
                  </Link>
                  <p className="text-sm text-slate-500">
                    {cuandoSubida(c.createdAt)} · base v{c.versionNumero}, al {fecha(c.fechaCorte)} · control {c.pctControl}%
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONOS[paso.tono]}`}>{paso.texto}</span>
              </div>
              {c.resultado && c.resultado.estado !== "preparada" ? (
                <ResultadoEnCorto resultado={c.resultado} grupos={c.grupos} />
              ) : (
                c.grupos.length > 0 && (
                  <span className="flex flex-wrap gap-2 text-sm">
                    {c.grupos.map((g, i) => (
                      <span key={g.id} className="rounded-full bg-violet-50 px-2.5 py-1 text-violet-800">
                        {nombreDelGrupo(g, i)} · {num(g.personas)}
                      </span>
                    ))}
                  </span>
                )
              )}
              <div className="flex justify-end">
                <Link to={paso.destino} className={btnSecundario}>
                  {paso.accion}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </Pagina>
  );
}
